import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Space, Divider, Typography, message, Tabs, Spin, Select, AutoComplete, Upload } from 'antd';
import { UserOutlined, TeamOutlined, SearchOutlined, ProfileOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useBiathlonEvent } from '../contexts/BiathlonEventContext';
import { db } from '../firebase';
import { collection, doc, setDoc, deleteDoc, getDocs, getDoc, query, where } from 'firebase/firestore';

const { Title, Text } = Typography;

// Helper function to recursively find an attribute in an object
function findAttribute(obj: any, key: string): any | undefined {
  if (obj && typeof obj === 'object') {
    if (key in obj) return obj[key];
    for (const k in obj) {
      const result = findAttribute(obj[k], key);
      if (result !== undefined) return result;
    }
  }
  return undefined;
}

interface SettingsModalProps {
  visible: boolean;
  onClose: () => void;
}

interface AthleteInfo {
  uid: number;
  name: string;
}

interface Innskytter {
  id: string;
  name: string;
  phone: string;
  email: string;
  athletes?: AthleteInfo[]; // Store athlete UID and name
}

interface Profile {
  id: string;
  name: string;
  description?: string;
  defaultFilters?: {
    clubs?: string[];
    teams?: string[];
  };
  innskyttere?: Innskytter[];
  setupData?: {
    [eventId: string]: {
      [puljename: string]: {
        setup: {
          startInnskyting?: string;
          sisteInnskyting?: string;
          forsteSkive?: number;
          sisteSkive?: number;
          skiveData?: {[key: number]: {innskytter: string, medhjelper: string}};
          tidPerPar?: string;
          showSkiveDetails?: boolean;
          utdelingStartnummerAmmo?: string;
          innpiskerTilInnskyting?: string;
          pappBytteFra?: string;
          pappBytteTil?: string;
          puljename?: string;
        };
        filters?: {
          clubs?: string[];
          teams?: string[];
          classes?: string[];
          nameSearch?: string;
          excludeBibs?: string;
          startTime?: string;
        };
        tableAssignments?: any;
      };
    };
  };
}

const SettingsModal: React.FC<SettingsModalProps> = ({ visible, onClose }) => {
  const { user } = useAuth();
  const { athletes, teams, clubs } = useBiathlonEvent();
  const [loading, setLoading] = useState(false);
  const [selectedInnskytter, setSelectedInnskytter] = useState<string | null>(null);
  const [innskyttere, setInnskyttere] = useState<Innskytter[]>([]);
  const [loadingInnskyttere, setLoadingInnskyttere] = useState(false);
  const [athleteSearchValue, setAthleteSearchValue] = useState('');
  const [currentProfileId, setCurrentProfileId] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<string | null>(null);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [profileHasUnsavedChanges, setProfileHasUnsavedChanges] = useState(false);
  const [originalProfileData, setOriginalProfileData] = useState<Profile | null>(null);

  // Load profiles and current profile's innskyttere
  useEffect(() => {
    if (visible && user) {
      loadProfiles();
      loadCurrentProfile();
    }
  }, [visible, user]);

  const loadCurrentProfile = async () => {
    if (!user) return;
    
    try {
      const selectedProfileDoc = await getDoc(doc(db, 'userSettings', user.uid));
      if (selectedProfileDoc.exists()) {
        const profileId = selectedProfileDoc.data().selectedProfileId;
        setCurrentProfileId(profileId);
        
        if (profileId) {
          const profileDoc = await getDoc(doc(db, 'profiles', profileId));
          if (profileDoc.exists()) {
            const profileData = profileDoc.data();
            const profileInnskyttere = profileData.innskyttere || [];
            
            // Migrate old format if needed
            const migratedInnskyttere = profileInnskyttere.map((innskytter: any) => {
              let athletesData = innskytter.athletes || [];
              if (athletesData.length > 0 && typeof athletesData[0] === 'number') {
                athletesData = athletesData.map((uid: number) => {
                  const athlete = athletes.find(a => a.Utover.UID === uid);
                  return {
                    uid,
                    name: athlete?.Utover.NavnFormatert || 'Unknown Athlete'
                  };
                });
              }
              return {
                ...innskytter,
                athletes: athletesData
              };
            });
            
            setInnskyttere(migratedInnskyttere);
          }
        }
      }
    } catch (error) {
      console.error('Error loading current profile:', error);
    }
  };

  const loadProfiles = async () => {
    if (!user) return;
    
    try {
      setLoadingProfiles(true);
      const q = query(
        collection(db, 'profiles'),
        where('userId', '==', user.uid)
      );
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Profile));
      setProfiles(data);
    } catch (error) {
      console.error('Error loading profiles:', error);
      message.error('Failed to load profiles');
    } finally {
      setLoadingProfiles(false);
    }
  };

  const saveProfile = async (profile: Profile) => {
    if (!user) return;
    
    try {
      const dataToSave = {
        name: profile.name,
        description: profile.description || '',
        defaultFilters: profile.defaultFilters || { clubs: [], teams: [] },
        innskyttere: profile.innskyttere || [],
        setupData: profile.setupData || {},
        userId: user.uid,
        updatedAt: new Date().toISOString()
      };
      await setDoc(doc(db, 'profiles', profile.id), dataToSave);
      message.success('Profile saved successfully');
    } catch (error) {
      console.error('Error saving profile:', error);
      message.error('Failed to save profile');
    }
  };

  const deleteProfile = async (id: string) => {
    if (!user) return;
    
    try {
      await deleteDoc(doc(db, 'profiles', id));
      setProfiles(profiles.filter(p => p.id !== id));
      setSelectedProfile(null);
      message.success('Profile deleted');
    } catch (error) {
      console.error('Error deleting profile:', error);
      message.error('Failed to delete profile');
    }
  };

  const handleExportProfile = async () => {
    if (!user || !selectedProfile) {
      message.error('No profile selected');
      return;
    }

    try {
      const profileDoc = await getDoc(doc(db, 'profiles', selectedProfile));
      if (!profileDoc.exists()) {
        message.error('Profile not found');
        return;
      }

      const profileData = profileDoc.data();

      // Get all setups for this profile
      const setupsSnapshot = await getDocs(collection(db, 'profiles', selectedProfile, 'setups'));
      const setups: any = {};
      setupsSnapshot.forEach(setupDoc => {
        setups[setupDoc.id] = setupDoc.data();
      });

      const exportData = {
        profile: {
          name: profileData.name,
          description: profileData.description || '',
          defaultFilters: profileData.defaultFilters || {},
          innskyttere: profileData.innskyttere || []
        },
        setups: setups,
        exportDate: new Date().toISOString(),
        version: '1.0'
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `profile-${profileData.name}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success('Profile exported successfully!');
    } catch (error) {
      console.error('Error exporting profile:', error);
      message.error('Failed to export profile');
    }
  };

  const handleImportProfile = (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const content = e.target?.result as string;
        const importedData = JSON.parse(content);
        
        if (!importedData.profile || typeof importedData.profile !== 'object') {
          message.error('Invalid profile file format');
          return;
        }

        if (!user || !selectedProfile) {
          message.error('No profile selected');
          return;
        }

        // Update the selected profile
        await setDoc(doc(db, 'profiles', selectedProfile), {
          name: importedData.profile.name,
          description: importedData.profile.description || '',
          defaultFilters: importedData.profile.defaultFilters || {},
          innskyttere: importedData.profile.innskyttere || [],
          userId: user.uid
        }, { merge: true });

        // Import all setups
        if (importedData.setups && typeof importedData.setups === 'object') {
          for (const [eventId, setupData] of Object.entries(importedData.setups)) {
            await setDoc(
              doc(db, 'profiles', selectedProfile, 'setups', eventId),
              setupData as any
            );
          }
        }

        // Reload profiles
        await loadProfiles();
        
        // Update the selected profile data in UI
        const profile = profiles.find(p => p.id === selectedProfile);
        if (profile) {
          profile.name = importedData.profile.name;
          profile.description = importedData.profile.description;
          profile.defaultFilters = importedData.profile.defaultFilters;
          profile.innskyttere = importedData.profile.innskyttere;
          setProfiles([...profiles]);
        }

        message.success('Profile imported successfully!');
      } catch (error) {
        console.error('Error importing profile:', error);
        message.error('Failed to import profile: invalid file or database error');
      }
    };
    reader.readAsText(file);
    return false;
  };

  const saveInnskytter = async (innskytter: Innskytter) => {
    if (!user || !currentProfileId) return;
    
    try {
      const profileRef = doc(db, 'profiles', currentProfileId);
      const profileDoc = await getDoc(profileRef);
      
      if (!profileDoc.exists()) {
        message.error('No profile selected');
        return;
      }

      const profileData = profileDoc.data();
      const updatedInnskyttere = innskyttere.map(i => 
        i.id === innskytter.id ? innskytter : i
      );

      await setDoc(profileRef, {
        ...profileData,
        innskyttere: updatedInnskyttere,
        updatedAt: new Date().toISOString()
      });
      
      message.success('Innskytter saved successfully');
    } catch (error) {
      console.error('Error saving innskytter:', error);
      message.error('Failed to save innskytter');
    }
  };

  const deleteInnskytter = async (id: string) => {
    if (!user || !currentProfileId) return;
    
    try {
      const profileRef = doc(db, 'profiles', currentProfileId);
      const profileDoc = await getDoc(profileRef);
      
      if (!profileDoc.exists()) return;

      const profileData = profileDoc.data();
      const updatedInnskyttere = innskyttere.filter(i => i.id !== id);

      await setDoc(profileRef, {
        ...profileData,
        innskyttere: updatedInnskyttere,
        updatedAt: new Date().toISOString()
      });
      
      setInnskyttere(updatedInnskyttere);
      setSelectedInnskytter(null);
      message.success('Innskytter deleted');
    } catch (error) {
      console.error('Error deleting innskytter:', error);
      message.error('Failed to delete innskytter');
    }
  };

  const handleUpdateProfile = async (values: { displayName: string }) => {
    try {
      setLoading(true);
      // TODO: Implement profile update with Firebase
      message.success('Profile updated successfully');
      onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      message.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const accountTab = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Title level={5}>Account Information</Title>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">Email:</Text>
            <br />
            <Text strong>{user?.email}</Text>
          </div>
          <div>
            <Text type="secondary">Account created:</Text>
            <br />
            <Text>{user?.metadata.creationTime}</Text>
          </div>
        </Space>
      </div>

      <Divider />

      <div>
        <Title level={5}>Profile Settings</Title>
        <Form
          layout="vertical"
          onFinish={handleUpdateProfile}
          initialValues={{
            displayName: user?.displayName || ''
          }}
        >
          <Form.Item
            label="Display Name"
            name="displayName"
            rules={[{ required: true, message: 'Please enter your display name' }]}
          >
            <Input placeholder="Enter your name" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Space>
  );

  const selectedInnskytterData = innskyttere.find(i => i.id === selectedInnskytter);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalData, setOriginalData] = useState<Innskytter | null>(null);

  // Track original data when selection changes
  useEffect(() => {
    if (selectedInnskytterData) {
      setOriginalData(JSON.parse(JSON.stringify(selectedInnskytterData)));
      setHasUnsavedChanges(false);
    }
  }, [selectedInnskytter]);

  const innskyttereTab = (
    <div style={{ display: 'flex', gap: '20px', minHeight: '400px' }}>
      <div style={{ flex: '0 0 200px', borderRight: '1px solid #f0f0f0', paddingRight: '20px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Button type="primary" block onClick={async () => {
            if (!currentProfileId) {
              message.error('Please select a profile first');
              return;
            }
            const newId = Date.now().toString();
            const newInnskytter = { 
              id: newId, 
              name: '', 
              phone: '', 
              email: '' 
            };
            const updatedInnskyttere = [...innskyttere, newInnskytter];
            setInnskyttere(updatedInnskyttere);
            setSelectedInnskytter(newId);
          }}>
            Add Innskytter
          </Button>
        </div>
        {loadingInnskyttere ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '500px', overflowY: 'auto' }}>
          {innskyttere.map(innskytter => (
            <div
              key={innskytter.id}
              onClick={() => setSelectedInnskytter(innskytter.id)}
              style={{
                padding: '6px 8px',
                cursor: 'pointer',
                borderRadius: '4px',
                backgroundColor: selectedInnskytter === innskytter.id ? '#e6f7ff' : 'transparent',
                border: selectedInnskytter === innskytter.id ? '1px solid #1890ff' : '1px solid transparent',
                transition: 'all 0.3s'
              }}
            >
              <Text strong>{innskytter.name}</Text>
            </div>
          ))}
        </div>
      </div>
      
      <div style={{ flex: 1, paddingLeft: '20px' }}>
        {selectedInnskytterData ? (
          <div style={{ display: 'flex', gap: '20px' }}>
            <div style={{ flex: '0 0 300px' }}>
              <Title level={5}>Innskytter Details</Title>
              <Form
                key={selectedInnskytter}
                layout="vertical"
                initialValues={selectedInnskytterData}
                onValuesChange={(_, values) => {
                  const updatedInnskytter = { ...selectedInnskytterData, ...values };
                  setInnskyttere(innskyttere.map(i => 
                    i.id === selectedInnskytter ? updatedInnskytter : i
                  ));
                  // Check if data has changed
                  const hasChanges = originalData && (
                    originalData.name !== updatedInnskytter.name ||
                    originalData.phone !== updatedInnskytter.phone ||
                    originalData.email !== updatedInnskytter.email
                  );
                  setHasUnsavedChanges(!!hasChanges);
                }}
              >
                <Form.Item
                  label="Name"
                  name="name"
                  rules={[{ required: true, message: 'Please enter name' }]}
                >
                  <Input placeholder="Enter name" autoFocus />
                </Form.Item>

                <Form.Item
                  label="Phone"
                  name="phone"
                >
                  <Input placeholder="Enter phone number" />
                </Form.Item>

                <Form.Item
                  label="Email"
                  name="email"
                  rules={[{ type: 'email', message: 'Please enter a valid email' }]}
                >
                  <Input placeholder="Enter email" />
                </Form.Item>

                <Form.Item>
                  <Space>
                    <Button 
                      type="primary"
                      disabled={!hasUnsavedChanges}
                      onClick={async () => {
                        if (selectedInnskytterData) {
                          await saveInnskytter(selectedInnskytterData);
                          setOriginalData(JSON.parse(JSON.stringify(selectedInnskytterData)));
                          setHasUnsavedChanges(false);
                        }
                      }}
                    >
                      Save Changes
                    </Button>
                    <Button 
                      danger
                      onClick={() => {
                        if (selectedInnskytter) {
                          deleteInnskytter(selectedInnskytter);
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </div>

            <Divider type="vertical" style={{ height: 'auto' }} />

            <div style={{ flex: 1 }}>
              <Title level={5}>Connected Athletes</Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
                Athletes from the selected event
              </Text>
              {selectedInnskytterData.athletes && selectedInnskytterData.athletes.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {selectedInnskytterData.athletes.map((athleteInfo: AthleteInfo) => {
                    const athlete = athletes.find(a => a.Utover.UID === athleteInfo.uid);
                    return (
                      <div 
                        key={athleteInfo.uid}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '4px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div>
                          <Text strong>{athleteInfo.name}</Text>
                          <br />
                          {athlete ? (
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {athlete.Utover.Klubb.Navn} - #{athlete.Startnummer}
                            </Text>
                          ) : (
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              Not in current event
                            </Text>
                          )}
                        </div>
                        <Button 
                          type="text" 
                          danger 
                          size="small"
                          onClick={() => {
                            const updatedAthletes = (selectedInnskytterData.athletes || []).filter(a => a.uid !== athleteInfo.uid);
                            const updatedInnskytter = { ...selectedInnskytterData, athletes: updatedAthletes };
                            setInnskyttere(innskyttere.map(i => 
                              i.id === selectedInnskytter ? updatedInnskytter : i
                            ));
                            saveInnskytter(updatedInnskytter);
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Text type="secondary">No athletes connected yet</Text>
              )}
              
              <div style={{ marginTop: '12px' }}>
                <AutoComplete
                  style={{ width: '100%' }}
                  value={athleteSearchValue}
                  onChange={setAthleteSearchValue}
                  placeholder="Search athlete by name"
                  options={athletes
                    .filter(a => {
                      const searchLower = athleteSearchValue.toLowerCase();
                      const fullName = a.Utover.NavnFormatert.toLowerCase();
                      const existingUIDs = (selectedInnskytterData.athletes || []).map(ai => ai.uid);
                      return fullName.includes(searchLower) && !existingUIDs.includes(a.Utover.UID);
                    })
                    .slice(0, 10)
                    .map(a => ({
                      value: a.Utover.NavnFormatert,
                      label: (
                        <div>
                          <div>{a.Utover.NavnFormatert}</div>
                          <div style={{ fontSize: '12px', color: '#888' }}>
                            {a.Utover.Klubb.Navn} - #{a.Startnummer}
                          </div>
                        </div>
                      ),
                      key: a.Utover.UID
                    }))}
                  onSelect={(value, option) => {
                    const athleteUID = option.key as number;
                    const athleteName = value;
                    const currentAthletes = selectedInnskytterData.athletes || [];
                    const existingUIDs = currentAthletes.map(ai => ai.uid);
                    if (!existingUIDs.includes(athleteUID)) {
                      const updatedInnskytter = { 
                        ...selectedInnskytterData, 
                        athletes: [...currentAthletes, { uid: athleteUID, name: athleteName }] 
                      };
                      setInnskyttere(innskyttere.map(i => 
                        i.id === selectedInnskytter ? updatedInnskytter : i
                      ));
                      saveInnskytter(updatedInnskytter);
                      setAthleteSearchValue('');
                    }
                  }}
                  suffixIcon={<SearchOutlined />}
                />
              </div>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', paddingTop: '50px' }}>
            <Text type="secondary">Select an innskytter from the list to view details</Text>
          </div>
        )}
      </div>
    </div>
  );

  const selectedProfileData = profiles.find(p => p.id === selectedProfile);

  // Track original profile data when selection changes
  useEffect(() => {
    if (selectedProfileData) {
      setOriginalProfileData(JSON.parse(JSON.stringify(selectedProfileData)));
      setProfileHasUnsavedChanges(false);
    }
  }, [selectedProfile]);

  const profilesTab = (
    <div style={{ display: 'flex', gap: '20px', minHeight: '400px' }}>
      <div style={{ flex: '0 0 200px', borderRight: '1px solid #f0f0f0', paddingRight: '20px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Button type="primary" block onClick={async () => {
            const newId = Date.now().toString();
            const newProfile: Profile = { 
              id: newId, 
              name: '', 
              description: '',
              defaultFilters: { clubs: [], teams: [] },
              innskyttere: [],
              setupData: {}
            };
            await saveProfile(newProfile);
            setProfiles([...profiles, newProfile]);
            setSelectedProfile(newId);
          }}>
            Add Profile
          </Button>
        </div>
        {loadingProfiles ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {profiles.map(profile => (
            <div
              key={profile.id}
              onClick={() => setSelectedProfile(profile.id)}
              style={{
                padding: '6px 8px',
                cursor: 'pointer',
                borderRadius: '4px',
                backgroundColor: selectedProfile === profile.id ? '#e6f7ff' : 'transparent',
                border: selectedProfile === profile.id ? '1px solid #1890ff' : '1px solid transparent',
                transition: 'all 0.3s'
              }}
            >
              <Text strong>{profile.name}</Text>
            </div>
          ))}
        </div>
      </div>
      
      <div style={{ flex: 1, paddingLeft: '20px' }}>
        {selectedProfileData ? (
          <div>
            <Title level={5}>Profile Details</Title>
            <Form
              key={selectedProfile}
              layout="vertical"
              initialValues={selectedProfileData}
              onValuesChange={(_, values) => {
                const updatedProfile = { ...selectedProfileData, ...values };
                setProfiles(profiles.map(p => 
                  p.id === selectedProfile ? updatedProfile : p
                ));
                // Check if data has changed
                const hasChanges = originalProfileData && (
                  originalProfileData.name !== updatedProfile.name ||
                  originalProfileData.description !== updatedProfile.description ||
                  JSON.stringify(originalProfileData.defaultFilters) !== JSON.stringify(updatedProfile.defaultFilters)
                );
                setProfileHasUnsavedChanges(!!hasChanges);
              }}
            >
              <Form.Item
                label="Profile Name"
                name="name"
                rules={[{ required: true, message: 'Please enter profile name' }]}
              >
                <Input placeholder="Enter profile name" autoFocus />
              </Form.Item>

              <Form.Item
                label="Description"
                name="description"
              >
                <Input.TextArea placeholder="Enter description (optional)" rows={2} />
              </Form.Item>

              <Divider />

              <Title level={5}>Default Filters</Title>

              <Form.Item
                label="Clubs"
                name={['defaultFilters', 'clubs']}
              >
                <Select
                  mode="multiple"
                  placeholder="Select clubs to filter by default"
                  allowClear
                  style={{ width: '100%' }}
                  options={clubs.map(club => ({
                    label: club,
                    value: club
                  }))}
                />
              </Form.Item>

              <Form.Item
                label="Teams"
                name={['defaultFilters', 'teams']}
              >
                <Select
                  mode="multiple"
                  placeholder="Select teams to filter by default"
                  allowClear
                  style={{ width: '100%' }}
                  options={teams.map(team => ({
                    label: team,
                    value: team
                  }))}
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button 
                    type="primary"
                    disabled={!profileHasUnsavedChanges}
                    onClick={async () => {
                      if (selectedProfileData) {
                        await saveProfile(selectedProfileData);
                        setOriginalProfileData(JSON.parse(JSON.stringify(selectedProfileData)));
                        setProfileHasUnsavedChanges(false);
                      }
                    }}
                  >
                    Save Changes
                  </Button>
                  <Button 
                    danger
                    onClick={() => {
                      if (selectedProfile) {
                        deleteProfile(selectedProfile);
                      }
                    }}
                  >
                    Delete
                  </Button>
                </Space>
              </Form.Item>

              <Divider />

              <Form.Item label="Export / Import Profile">
                <Space>
                  <Button 
                    icon={<DownloadOutlined />}
                    onClick={handleExportProfile}
                    disabled={!selectedProfile}
                  >
                    Export Profile
                  </Button>
                  <Upload
                    accept=".json"
                    beforeUpload={handleImportProfile}
                    showUploadList={false}
                  >
                    <Button 
                      icon={<UploadOutlined />}
                      disabled={!selectedProfile}
                    >
                      Import Profile
                    </Button>
                  </Upload>
                </Space>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                  Includes: Innskyttere, filters, and all event setups
                </div>
              </Form.Item>
            </Form>
          </div>
        ) : (
          <div style={{ textAlign: 'center', paddingTop: '50px' }}>
            <Text type="secondary">Select a profile from the list to view details</Text>
          </div>
        )}
      </div>
    </div>
  );

  const tabItems = [
    {
      key: 'account',
      label: (
        <span>
          <UserOutlined /> Account
        </span>
      ),
      children: accountTab
    },
    {
      key: 'innskyttere',
      label: (
        <span>
          <TeamOutlined /> Innskyttere
        </span>
      ),
      children: innskyttereTab
    },
    {
      key: 'profiles',
      label: (
        <span>
          <ProfileOutlined /> Profiles
        </span>
      ),
      children: profilesTab
    }
  ];

  return (
    <Modal
      title="Settings"
      open={visible}
      onCancel={onClose}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button onClick={onClose} type="primary">
            Close
          </Button>
        </div>
      }
      width={900}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      <Tabs defaultActiveKey="account" items={tabItems} />
    </Modal>
  );
};

export default SettingsModal;
